import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Button, Segmented, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import 'moment-jalaali';

/**
 * تبدیل تاریخ شمسی API (1404/05/06) به میلادی YYYY-MM-DD برای FullCalendar
 */
const jalaliApiToGregorian = (value) => {
  if (!value) return null;
  const raw = String(value).trim().replace(/-/g, '/');
  const m = moment(raw, 'jYYYY/jMM/jDD');
  if (!m.isValid()) {
    const g = moment(raw, ['YYYY-MM-DD', 'YYYY/MM/DD'], true);
    if (g.isValid()) return g.format('YYYY-MM-DD');
    return null;
  }
  return m.format('YYYY-MM-DD');
};

const ProjectCalendarView = ({
  events = [],
  loading = false,
  onRangeChange,
  onEventClick,
}) => {
  const calendarRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(moment());
  const [viewType, setViewType] = useState('month');

  const visibleRange = useMemo(() => {
    if (viewType === 'week') {
      const day = currentDate.day();
      const daysFromSat = day === 6 ? 0 : day + 1;
      const startOfWeek = currentDate.clone().subtract(daysFromSat, 'days');
      return {
        start: startOfWeek.format('YYYY-MM-DD'),
        end: startOfWeek.clone().add(7, 'days').format('YYYY-MM-DD'),
        apiStart: startOfWeek.format('jYYYY/jMM/jDD'),
        apiEnd: startOfWeek.clone().add(6, 'days').format('jYYYY/jMM/jDD'),
      };
    }
    const jMonthStart = currentDate.clone().startOf('jMonth');
    const jMonthEnd = currentDate.clone().endOf('jMonth');
    const startDay = jMonthStart.day();
    const daysFromSat = startDay === 6 ? 0 : startDay + 1;
    const gridStart = jMonthStart.clone().subtract(daysFromSat, 'days');
    const gridEnd = gridStart.clone().add(42, 'days');
    return {
      start: gridStart.format('YYYY-MM-DD'),
      end: gridEnd.format('YYYY-MM-DD'),
      apiStart: jMonthStart.format('jYYYY/jMM/jDD'),
      apiEnd: jMonthEnd.format('jYYYY/jMM/jDD'),
    };
  }, [currentDate, viewType]);

  useEffect(() => {
    onRangeChange?.({
      start: visibleRange.apiStart,
      end: visibleRange.apiEnd,
    });
  }, [visibleRange.apiStart, visibleRange.apiEnd, onRangeChange]);

  const fcEvents = useMemo(
    () =>
      (events || [])
        .map((ev) => {
          const start = jalaliApiToGregorian(ev.start);
          const endRaw = jalaliApiToGregorian(ev.end);
          if (!start) return null;
          const end = endRaw
            ? moment(endRaw, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD')
            : undefined;
          return {
            id: String(ev.id),
            title: ev.title || '—',
            start,
            end,
            backgroundColor: ev.color || '#1677ff',
            borderColor: ev.color || '#1677ff',
            textColor: '#fff',
            extendedProps: ev,
          };
        })
        .filter(Boolean),
    [events],
  );

  const handlePrev = () => {
    setCurrentDate((prev) =>
      viewType === 'week' ? prev.clone().subtract(1, 'week') : prev.clone().subtract(1, 'jMonth'),
    );
  };

  const handleNext = () => {
    setCurrentDate((prev) =>
      viewType === 'week' ? prev.clone().add(1, 'week') : prev.clone().add(1, 'jMonth'),
    );
  };

  const headerTitle =
    viewType === 'week'
      ? `${currentDate.clone().startOf('week').format('jD jMMMM')} – ${currentDate
          .clone()
          .endOf('week')
          .format('jD jMMMM jYYYY')}`
      : currentDate.format('jMMMM jYYYY');

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button icon={<RightOutlined />} onClick={handlePrev} style={{ fontFamily: 'CustomFont' }} />
          <Button onClick={() => setCurrentDate(moment())} style={{ fontFamily: 'CustomFont' }}>
            امروز
          </Button>
          <Button icon={<LeftOutlined />} onClick={handleNext} style={{ fontFamily: 'CustomFont' }} />
          <span className="text-base font-semibold mr-2" style={{ fontFamily: 'CustomFont' }}>
            {headerTitle}
          </span>
        </div>
        <Segmented
          value={viewType}
          onChange={setViewType}
          options={[
            { label: 'ماه', value: 'month' },
            { label: 'هفته', value: 'week' },
          ]}
          style={{ fontFamily: 'CustomFont' }}
        />
      </div>

      {!loading && !fcEvents.length ? (
        <p className="text-sm text-gray-500 mb-3 m-0" style={{ fontFamily: 'CustomFont' }}>
          پروژه‌ای در این بازه ثبت نشده است
        </p>
      ) : null}

      <Spin spinning={loading}>
        <FullCalendar
          key={`${visibleRange.start}_${visibleRange.end}_${viewType}`}
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView={viewType === 'week' ? 'dayGridWeek' : 'dayGridMonth'}
          headerToolbar={false}
          locale="fa"
          direction="rtl"
          height="auto"
          visibleRange={{
            start: visibleRange.start,
            end: visibleRange.end,
          }}
          events={fcEvents}
          eventClick={(info) => {
            onEventClick?.(info.event.extendedProps);
          }}
          displayEventTime={false}
        />
      </Spin>
    </div>
  );
};

export default ProjectCalendarView;
